import Service from '@ember/service';
import { service } from '@ember/service';
import type SessionContextService from './session-context';
import type EmployeeService from './employee';
import type RouterService from '@ember/routing/router-service';
import type FirebaseAnalyticsService from './firebase-analytics';
import { setUserId, setUserProperties, logEvent } from 'firebase/analytics';

export default class GoogleAnalyticsService extends Service {
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare router: RouterService;
  @service declare firebaseAnalytics: FirebaseAnalyticsService;

  // Track if we've set user properties for this session
  private hasSetUserProperties = false;

  // Routes that should not be tracked automatically
  private excludedRoutes = new Set([
    'login',
    'logout',
    'reset-password',
    'register',
    'error',
    'loading',
    'admin',
  ]);

  /**
   * Initialize analytics tracking for the current session
   * Should be called once when the user authenticates
   */
  initializeTracking() {
    if (!this.hasSetUserProperties && this.sessionContext.isFullyAuthenticated) {
      this.trackUserProperties();
      this.hasSetUserProperties = true;
    }
  }

  /**
   * Track page views with user properties
   * @param title - The page title
   */
  trackPageView(title?: string) {
    // Check if current route should be excluded from tracking
    const currentRoute = this.router.currentRouteName;
    if (currentRoute && this.isRouteExcluded(currentRoute)) {
      return;
    }

    const analytics = this.firebaseAnalytics.analytics;
    if (analytics) {
      logEvent(analytics, 'page_view', {
        page_title: title,
        page_location: window.location.href,
      });
    }
  }

  /**
   * Track user properties and login event
   */
  trackUserProperties() {
    if (this.hasSetUserProperties) return;

    const currentEmployee = this.employee.current;
    const personId = this.sessionContext.person?.id;

    if (!personId || !this.firebaseAnalytics.isInitialized) {
      return;
    }

    const userId = `p-${personId}`;
    const userProps: Record<string, string | undefined> = {};

    // Add employee properties if available
    if (currentEmployee && currentEmployee.id) {
      // Access employee attributes safely - checking for merged attributes or direct properties
      const employeeData = currentEmployee as {
        customAttributes?: {
          merged?: {
            department?: string;
            title?: string;
            employment_type?: string;
            employmentType?: string;
            hire_date?: string;
            employmentStartDate?: string;
          };
        };
        department?: string;
        title?: string;
        employment_type?: string;
        hire_date?: string;
      };

      if (employeeData.customAttributes?.merged) {
        const merged = employeeData.customAttributes.merged;
        if (merged.department && typeof merged.department === 'string') {
          userProps.department = merged.department;
        }
        if (merged.title && typeof merged.title === 'string') {
          userProps.title = merged.title;
        }
        if (merged.employmentType && typeof merged.employmentType === 'string') {
          userProps.employment_type = merged.employmentType;
        }
        if (merged.employmentStartDate && typeof merged.employmentStartDate === 'string') {
          userProps.hire_date = merged.employmentStartDate;
        }
      }

      // Also check for direct properties on the employee model
      if (employeeData.department && typeof employeeData.department === 'string') {
        userProps.department = employeeData.department;
      }
      if (employeeData.title && typeof employeeData.title === 'string') {
        userProps.title = employeeData.title;
      }
    }

    // Set user ID using Firebase Analytics
    const analytics = this.firebaseAnalytics.analytics;
    if (!analytics) {
      return; // Skip if analytics not available
    }

    setUserId(analytics, userId);

    // Set user properties using Firebase Analytics
    setUserProperties(analytics, userProps);

    // Send a login event with user properties
    logEvent(analytics, 'login', {
      method: 'tio_authentication',
      people_id: userId,
      department: userProps.department,
      title: userProps.title,
      employment_type: userProps.employment_type,
      hire_date: userProps.hire_date,
    });

    this.hasSetUserProperties = true;
  }

  /**
   * Track custom events with person ID
   * @param eventName - The custom event name
   * @param eventParameters - Additional event parameters
   */
  trackEvent(eventName: string, eventParameters: Record<string, unknown> = {}) {
    const personId = this.sessionContext.person?.id;
    const analytics = this.firebaseAnalytics.analytics;

    if (analytics) {
      const eventData = {
        people_id: personId ? `p-${personId}` : undefined,
        ...eventParameters,
      };

      logEvent(analytics, eventName, eventData);
    }
  }

  /**
   * Complete tracking for a route transition
   * Call this from routes that want comprehensive analytics
   */
  trackRouteTransition(routeName?: string, additionalData: Record<string, unknown> = {}) {
    // Ensure user properties are set
    this.initializeTracking();

    // Track the page view
    this.trackPageView(routeName);

    // Track any additional route-specific data
    if (Object.keys(additionalData).length > 0) {
      this.trackEvent('route_engagement', {
        route_name: routeName || this.router.currentRouteName,
        ...additionalData,
      });
    }
  }

  /**
   * Reset tracking state (useful for testing or user logout)
   */
  reset() {
    this.hasSetUserProperties = false;
  }

  /**
   * Add a route to the exclusion list
   */
  excludeRoute(routeName: string) {
    this.excludedRoutes.add(routeName);
  }

  /**
   * Remove a route from the exclusion list
   */
  includeRoute(routeName: string) {
    this.excludedRoutes.delete(routeName);
  }

  /**
   * Check if a route is excluded from tracking
   */
  isRouteExcluded(routeName: string): boolean {
    return this.excludedRoutes.has(routeName);
  }
}
